import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate, Outlet } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { LanguageProvider, useLang, LOCALES, detectLocale } from "@/context/LanguageContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { LocaleNudge } from "@/components/LocaleNudge";
import { useGtagPageviews } from "@/lib/useGtagPageviews";
import { installClickTracking } from "@/lib/analytics";
import { CookieConsent } from "@/components/CookieConsent";
import { ChatWidget } from "@/components/ChatWidget";
import { Seo } from "@/components/Seo";
import { Toaster } from "@/components/ui/sonner";
import { waHref } from "@/i18n/translations";
import Home from "@/pages/Home";
import Properties from "@/pages/Properties";
import PropertyDetail from "@/pages/PropertyDetail";
import PropertyManagement from "@/pages/PropertyManagement";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import LegalMentions from "@/pages/LegalMentions";
import BlogList from "@/pages/BlogList";
import BlogPost from "@/pages/BlogPost";
import LandingPage from "@/pages/LandingPage";
import Press from "@/pages/Press";
import RivieraDataReport from "@/pages/RivieraDataReport";
import BestPropertyManagementNice from "@/pages/BestPropertyManagementNice";
import AuthorGorkem from "@/pages/AuthorGorkem";
import BuyerFlow from "@/pages/BuyerFlow";
import OwnerFlow from "@/pages/OwnerFlow";
import InvestorFlow from "@/pages/InvestorFlow";
import FlowThankYou from "@/pages/FlowThankYou";
import AuthCallback from "@/pages/admin/AuthCallback";
import AdminLogin from "@/pages/admin/AdminLogin";
import RequireAdmin from "@/pages/admin/RequireAdmin";
import AdminDashboard from "@/pages/admin/AdminDashboard";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!window.location.hash) window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const FloatingWhatsApp = () => {
  const { t } = useLang();
  return (
    <a
      data-testid="floating-whatsapp"
      href={waHref(t("contact.whatsappText"))}
      target="_blank"
      rel="nofollow noopener noreferrer"
      aria-label="WhatsApp"
      className="fixed bottom-6 right-6 z-40 w-14 h-14 flex items-center justify-center bg-primary text-primary-foreground shadow-lg hover:scale-105 transition-transform duration-300"
    >
      <MessageCircle size={24} />
    </a>
  );
};

// Path patterns that should NOT be redirected into the locale tree — either they
// contain URL-template placeholders (e.g. `/{website}/sso`) that never existed on
// this site, or they are legacy CMS paths surfaced in Google Search Console as
// "Server error" / "Page with redirect". Serving these paths a noindex 404-style
// response lets Google remove them from the index instead of chasing redirects.
const JUNK_PATH_PATTERNS = [
  /\{[^/]+\}/,             // literal template placeholders like {website} or {id}
  /^\/website(\/|$)/i,     // /website/*
  /^\/collection(\/|$)/i,  // /collection/*
  /^\/v1(\/|$)/i,          // old API-doc paths
  /^\/sso(\/|$)/i,         // /sso, /sso/*
  /^\/Services(\/|$)/,     // legacy TitleCase paths
  /^\/Our-Values(\/|$)/,
  /^\/docs(\/|$)/i,
  /^\/openapi\.json$/i,
  /^\/redoc(\/|$)/i,
];

const isJunkPath = (pathname) =>
  JUNK_PATH_PATTERNS.some((rx) => rx.test(pathname));

// Shared UI for junk / never-existed URLs. Emits noindex so Google removes the
// URL from the index, and offers a homepage link so accidental human visitors
// have somewhere to go.
const JunkNotFound = () => (
  <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 text-center">
    <Seo
      title="Page not found | Velmira Living"
      description="This page does not exist on velmiraliving.com."
      noindex
    />
    <h1 className="font-headings text-4xl mb-4">Page not found</h1>
    <p className="font-body text-foreground/70 max-w-md mb-8">
      The URL you followed does not exist on Velmira Living. Head back to our homepage.
    </p>
    <a
      href={`/${detectLocale()}`}
      className="inline-flex items-center gap-2 border border-primary/60 hover:border-primary text-primary px-6 py-3 uppercase tracking-widest text-xs transition-all"
      data-testid="not-found-home-link"
    >
      Go to homepage
    </a>
  </div>
);

// Redirects bare / legacy (non-prefixed) paths to the detected locale — unless
// the path matches a known junk pattern, in which case we render a noindex 404
// so Google removes it from the index.
const LocaleRedirect = () => {
  const { pathname, search, hash } = useLocation();
  if (isJunkPath(pathname)) return <JunkNotFound />;
  const rest = pathname === "/" ? "" : pathname;
  return <Navigate to={`/${detectLocale()}${rest}${search}${hash}`} replace />;
};

// Guards the :lang segment. If it is not a valid locale, treat it as a legacy
// path and redirect to the default locale, preserving the original path.
// Short-circuits junk template URLs (e.g. /website/{website}/sso) to the
// noindex 404 so the URL is NOT locale-prefixed and gets DEindexed cleanly.
const LangGate = () => {
  const { lang } = useParams();
  const { pathname, search, hash } = useLocation();
  if (!LOCALES.includes(lang)) {
    // Spanish locale retired: /es/* permanently points to the English equivalent.
    if (lang === "es") return <Navigate to={`/en${pathname.replace(/^\/es(?=\/|$)/, "")}${search}${hash}`} replace />;
    if (isJunkPath(pathname)) return <JunkNotFound />;
    return <Navigate to={`/${detectLocale()}${pathname}${search}${hash}`} replace />;
  }
  return <Outlet />;
};

const AppShell = () => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  useGtagPageviews();
  useEffect(() => installClickTracking(), []);

  // Prevent browser auto-translate (Chrome/Edge) on admin routes — auto-translation
  // rewrites text nodes while React reconciles and throws `insertBefore` errors.
  // The public site MUST stay translatable, so we scope this to /admin only.
  useEffect(() => {
    const html = document.documentElement;
    let googleMeta = document.head.querySelector('meta[name="google"][content="notranslate"]');
    if (isAdmin) {
      html.setAttribute("translate", "no");
      html.classList.add("notranslate");
      if (!googleMeta) {
        googleMeta = document.createElement("meta");
        googleMeta.setAttribute("name", "google");
        googleMeta.setAttribute("content", "notranslate");
        googleMeta.setAttribute("data-admin-notranslate", "1");
        document.head.appendChild(googleMeta);
      }
    } else {
      html.removeAttribute("translate");
      html.classList.remove("notranslate");
      const m = document.head.querySelector('meta[data-admin-notranslate="1"]');
      if (m) m.remove();
    }
  }, [isAdmin]);

  // OAuth return: process session_id from the URL fragment before anything else.
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      {!isAdmin && <Header />}
      <main>
        <Routes>
          <Route path="admin/login" element={<AdminLogin />} />
          <Route path="admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
          <Route path=":lang" element={<LangGate />}>
            <Route index element={<Home />} />
            <Route path="properties" element={<Properties />} />
            <Route path="properties/:id" element={<PropertyDetail />} />
            <Route path="buy" element={<BuyerFlow />} />
            <Route path="owner" element={<OwnerFlow />} />
            <Route path="invest" element={<InvestorFlow />} />
            <Route path="buy/thank-you" element={<FlowThankYou flow="buy" />} />
            <Route path="owner/thank-you" element={<FlowThankYou flow="owner" />} />
            <Route path="invest/thank-you" element={<FlowThankYou flow="invest" />} />
            <Route path="property-management" element={<PropertyManagement />} />
            <Route path="about" element={<About />} />
            <Route path="contact" element={<Contact />} />
            <Route path="mentions-legales" element={<LegalMentions />} />
            <Route path="legal-notice" element={<LegalMentions />} />
            <Route path="yasal-bilgiler" element={<LegalMentions />} />
            <Route path="blog" element={<BlogList />} />
            <Route path="blog/:slug" element={<BlogPost />} />
            <Route path="press" element={<Press />} />
            <Route path="riviera-real-estate-data-2026" element={<RivieraDataReport />} />
            <Route path="best-property-management-nice" element={<BestPropertyManagementNice />} />
            <Route path="author/gorkem" element={<AuthorGorkem />} />
            <Route path=":slug" element={<LandingPage />} />
            <Route path="*" element={<LandingPage />} />
          </Route>
          <Route path="*" element={<LocaleRedirect />} />
        </Routes>
      </main>
      {!isAdmin && <Footer />}
      {!isAdmin && <FloatingWhatsApp />}
      {!isAdmin && <ChatWidget />}
      {!isAdmin && <LocaleNudge />}
      {!isAdmin && <CookieConsent />}
      <Toaster position="top-center" theme="dark" />
    </>
  );
};

function App() {
  return (
    <div className="App min-h-screen bg-background">
      <BrowserRouter>
        <LanguageProvider>
          <AppShell />
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
